// @file Network requests related to user integrations from surface

import FetchableObject from '@@/surface/api/fetchable_object'
import { fetchJson } from '@@/surface/api_fetch'
import type {
  GoogleDriveAuthorizationResult,
  MicrosoftOneDriveAuthorizationResult,
  MicrosoftOneDriveResolvedDriveItem,
  UserId,
} from '@@/types'
import type { JsonAPIResponse } from '@padlet/arvo'
import { HTTPMethod } from '@padlet/fetch'

class UserIntegrationsApi extends FetchableObject {
  public static async fetchGoogleDriveAuthorization(
    id: UserId,
    fetchOptions = { apiVersion: 1 },
  ): Promise<JsonAPIResponse<GoogleDriveAuthorizationResult>> {
    const { apiVersion } = fetchOptions
    return await fetchJson(`/api/${apiVersion}/user/${id}/user_integrations/google_drive`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async refreshGoogleDriveAccessToken(
    id: UserId,
    fetchOptions = {},
  ): Promise<JsonAPIResponse<GoogleDriveAuthorizationResult>> {
    return await fetchJson(`/api/1/user/${id}/user_integrations/google_drive/refresh_access_token`, {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async fetchMicrosoftOneDriveAuthorization(
    id: UserId,
    fetchOptions = { apiVersion: 1 },
  ): Promise<JsonAPIResponse<MicrosoftOneDriveAuthorizationResult>> {
    const { apiVersion } = fetchOptions
    return await fetchJson(`/api/${apiVersion}/user/${id}/user-integrations/microsoft-onedrive`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async refreshMicrosoftOneDriveAccessToken(
    id: UserId,
    fetchOptions = {},
  ): Promise<JsonAPIResponse<MicrosoftOneDriveAuthorizationResult>> {
    return await fetchJson(`/api/1/user/${id}/user-integrations/microsoft-onedrive/refresh-access-token`, {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  // Resolves a pasted OneDrive/SharePoint sharing URL with the user's existing connection.
  // Rejects (404) when the user is not connected or the link is not readable with their token.
  public static async resolveMicrosoftOneDriveSharingUrl(
    id: UserId,
    sharingUrl: string,
    fetchOptions = {},
  ): Promise<JsonAPIResponse<MicrosoftOneDriveResolvedDriveItem>> {
    return await fetchJson(`/api/1/user/${id}/user-integrations/microsoft-onedrive/resolve-sharing-url`, {
      method: HTTPMethod.post,
      jsonData: { sharing_url: sharingUrl },
      ...fetchOptions,
    })
  }
}

export { UserIntegrationsApi }
